/**
 * フリーズフレーム (シーンの途中で 1 フレームを静止させて「ため」を作る) の正本。
 * docs/features/video-editor-capability-gaps-plan.md §4.1-L。
 *
 * 同じ意味・同じ数式の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出し)
 * - video-editor リポジトリの src/schema/freeze.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/freeze.py (AI 提案の正規化・合成フレーム・要約)
 *
 * ## 意味
 * - `atSourceSec`: 静止させるフレームの位置を**素材秒**で持つ (シーンの inSec〜outSec の内側)。
 *   表示秒ではなく素材秒なのは、トリム (in/out) や速度 (speed) を変えても「どのコマで止めるか」が
 *   ずれないため (§0-3 の直交規則)。
 * - `holdSec`: 静止している長さ (0.2〜5.0 秒・0.1 刻み・既定 1.0)。
 *
 * ## 時間モデル
 * 表示尺 D = (outSec - inSec) / speed + holdSec。表示時間軸では
 *
 *   [0, tA)            … 通常再生 (tA = (atSourceSec - inSec) / speed)
 *   [tA, tA + hold)    … 静止 (atSourceSec のコマを保持・**音声なし**)
 *   [tA + hold, D)     … atSourceSec から続きを再生
 *
 * テロップ・オーバーレイの秒は**表示時間軸のまま**なので、静止している間も指定どおり表示される。
 * ズーム / パン (motion) の進行 p も表示時間全体に対する相対なので、静止中も動き続ける。
 *
 * ## 排他
 * 速度ランプ (speedRamp) とは**排他**にする (ランプは表示秒 → 素材秒の写像が非線形で、
 * tA を求めるのに逆関数が要る)。書き込み経路 (AI の正規化・ops・保存時検証・UI) のすべてで
 * 「ランプのあるシーンにはフリーズを置けない」と明示的に弾く。静止画クリップにも付けない。
 */
import { z } from 'zod';
import { formatDurationLabel, formatTimecode } from './timecode';

/** 静止の長さの範囲 (秒)。短すぎると気づかれず、長すぎると間延びする。 */
export const FREEZE_HOLD_MIN_SEC = 0.2;
export const FREEZE_HOLD_MAX_SEC = 5;
/** 静止の長さの刻み (秒)。 */
export const FREEZE_HOLD_STEP_SEC = 0.1;
/** UI / ops で holdSec を省略したときの既定。 */
export const DEFAULT_FREEZE_HOLD_SEC = 1;

/**
 * 保存形。**範囲も必須も縛らない** (壊れた JSON でプロジェクトごと開けなくならないよう、
 * クランプ・丸め・欠落の判定は normalizeFreeze / clampFreezeToRange が行う。
 * LoudnessSchema・DuckingSchema と同じ方針で、数値キーはすべて省略可にしてある。
 * 欠けた値を持つオブジェクトは読み出し側 (`itemFreeze`) が「フリーズなし」として落とす)。
 */
export const FreezeSchema = z.object({
  atSourceSec: z.number().optional(),
  holdSec: z.number().optional(),
});
export type Freeze = z.infer<typeof FreezeSchema>;

/** 正規化後のフリーズ。数値キーは必ず埋まる (保存形の `Freeze` は省略可)。 */
export interface ResolvedFreeze {
  atSourceSec: number;
  holdSec: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** 小数 2 桁への half-up 丸め (backend の freeze.py の `_round2` と同じ規則。-0 は 0 に正規化)。 */
function round2(value: number): number {
  const rounded = Math.floor(value * 100 + 0.5) / 100;
  return rounded === 0 ? 0 : rounded;
}

/** 0.1 刻みへの half-up 丸め。 */
function roundHoldStep(value: number): number {
  const rounded = Math.floor(value * 10 + 0.5) / 10;
  return rounded === 0 ? 0 : rounded;
}

function finiteNumber(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value;
}

/**
 * 外部入力 (AI の提案・古い保存データ・UI の数値) を保存形へ正規化する。
 * `atSourceSec` は非負・小数 2 桁、`holdSec` は 0.2〜5.0 の 0.1 刻みへ丸める。
 * どちらかが数値でなければ undefined (= フリーズなし)。
 *
 * **素材の区間 (inSec〜outSec) へのクランプはここでは行わない** (このモジュールは
 * アイテムを知らないため)。区間へ収めるのは clampFreezeToRange の役割。
 */
export function normalizeFreeze(value: unknown): ResolvedFreeze | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const rawAt = finiteNumber(raw.atSourceSec);
  if (rawAt === null) return undefined;
  const rawHold = finiteNumber(raw.holdSec);
  const holdSec = roundHoldStep(
    clamp(rawHold === null ? DEFAULT_FREEZE_HOLD_SEC : rawHold, FREEZE_HOLD_MIN_SEC, FREEZE_HOLD_MAX_SEC),
  );
  return { atSourceSec: round2(Math.max(0, rawAt)), holdSec };
}

/**
 * フリーズ位置をシーンの素材区間 [inSec, outSec] の内側へ寄せる。
 * 区間が空 (in >= out) なら undefined (= フリーズなし)。
 */
export function clampFreezeToRange(
  freeze: Freeze | null | undefined,
  inSec: number,
  outSec: number,
): ResolvedFreeze | undefined {
  if (!freeze) return undefined;
  if (!(outSec > inSec)) return undefined;
  // 数値キーが欠けた / 壊れた保存データはここで落とす (スキーマでは縛らない方針のため)
  const at = finiteNumber(freeze.atSourceSec);
  const hold = finiteNumber(freeze.holdSec);
  if (at === null || hold === null) return undefined;
  return { atSourceSec: round2(clamp(at, inSec, outSec)), holdSec: hold };
}

/**
 * UI / 要約 / README に出す 1 行 (backend の describe_freeze と同じ文言)。
 * `fps` を渡すと画面向けに書く (位置は "MM:SS:FF"、長さは "S:FF" (秒:フレーム))。
 */
export function freezeLabel(
  freeze: Freeze | null | undefined,
  displaySecAt?: number,
  fps?: number,
): string {
  if (!freeze) return 'なし';
  if (fps !== undefined) {
    const at = displaySecAt === undefined ? '' : `表示 ${formatTimecode(displaySecAt, fps)} で`;
    return `${at}${formatDurationLabel(finiteNumber(freeze.holdSec) ?? 0, fps)}静止`;
  }
  const where = displaySecAt === undefined ? '' : `表示 ${displaySecAt.toFixed(1)} 秒で`;
  return `${where}${(finiteNumber(freeze.holdSec) ?? 0).toFixed(1)} 秒静止`;
}
